import { useEffect, useRef, useState } from 'react';
import { audioChannelsForPrompt, DIRECTION_DIALOGUE_PAUSE_MS } from '../core/audioRouting.js';
import { DIRECTION_VOICE_ID } from '../core/characterVoices.js';
import { normalizeVoiceSpeed, isSupportedVoiceSpeed, applyPitchPreservingSpeed } from '../core/voiceSpeed.js';
import { synchronizedPlaybackDelayMs, synchronizedStartTimestamp, synchronizedAudioContextStartTime } from './synchronizedPlayback.js';
import { createSerialAudioQueue } from './audioQueue.js';
import { audioContextNeedsReplacement, ensureAudioContextRunning } from './audioContextRecovery.js';
import { inspectPcmWav, friendlyPlaybackError, logAudioStage } from './audioDiagnostics.js';
import { readBoundedArrayBuffer } from '../../cloudflare/requestBody.js';
import { PREPARED_LINE_BYTES } from '../core/preparedSpeech.js';
import { sessionApiUrl } from '../realtime/sessionEndpoint.js';
import { validSceneIntroPath, prependSceneIntro } from './sceneIntroAudio.js';

const AUDIO_REQUEST_TIMEOUT_MS = 90 * 1000;
const SETTINGS_KEY = 'rehearsal-director:audio-settings';
function savedAudioSettings() {
  try { return JSON.parse(localStorage.getItem(SETTINGS_KEY) || '{}'); } catch { return {}; }
}
function confirmationWav() {
  const buffer = new ArrayBuffer(11564), bytes = new Uint8Array(buffer), view = new DataView(buffer);
  const ascii = (offset, value) => [...value].forEach((letter, index) => bytes[offset + index] = letter.charCodeAt(0));
  ascii(0, 'RIFF'); view.setUint32(4, 11556, true); ascii(8, 'WAVEfmt '); view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); view.setUint16(22, 1, true); view.setUint32(24, 24000, true);
  view.setUint32(28, 48000, true); view.setUint16(32, 2, true); view.setUint16(34, 16, true);
  ascii(36, 'data'); view.setUint32(40, 11520, true);
  for (let i = 0; i < 5760; i++) view.setInt16(44 + i * 2, Math.round(Math.sin(i * Math.PI * 2 * 660 / 24000) * 6500 * Math.sin(i * Math.PI / 5760)), true);
  return buffer;
}
function playbackOwner(settings, publish, setPaused) {
  return { alive: true, settings, publish, setPaused, queue: createSerialAudioQueue(), generation: 0, intent: 0,
    paused: false, context: null, media: null, hintMedia: null, hint: null, hintEpoch: 0, job: null, waits: new Set(), urls: new Set() };
}
function contextFor(owner) {
  const Constructor = window.AudioContext || window.webkitAudioContext;
  if (!Constructor) return null;
  if (audioContextNeedsReplacement(owner.context)) owner.context = new Constructor();
  return owner.context;
}
function mediaFor(owner, hint = false) {
  const name = hint ? 'hintMedia' : 'media';
  if (!owner[name]) owner[name] = new Audio();
  return owner[name];
}
function owned(owner, job) { return owner.alive && owner.generation === job.generation && !job.controller.signal.aborted; }
function stage(owner, job, value) {
  if (!owned(owner, job)) return;
  job.stage = value; owner.publish(owner.paused ? 'paused' : value, '');
}
function wait(owner, job, delay) {
  return new Promise(resolve => {
    const done = value => { if (!owner.waits.delete(done)) return; window.clearTimeout(timer); resolve(value); };
    const timer = delay == null ? null : window.setTimeout(() => done(owned(owner, job)), delay);
    done.pauseWait = delay == null; owner.waits.add(done);
  });
}
async function waitUntilResumed(owner, job) {
  while (owned(owner, job) && owner.paused) if (!await wait(owner, job)) return false;
  return owned(owner, job);
}
async function runningContext(owner, job, initial) {
  if (!await waitUntilResumed(owner, job)) return false;
  const context = contextFor(owner); if (!context) return true;
  const outcome = initial || ensureAudioContextRunning(context, {
    onResumeSettled() { if (!owner.alive || owner.paused) void context.suspend?.(); },
  });
  let running;
  try { running = await Promise.race([outcome, job.cancelled]); } catch { running = false; }
  if (!owned(owner, job)) return false;
  if (owner.paused) { await waitUntilResumed(owner, job); running = context.state === 'running'; }
  if (!running && context.state !== 'running') throw Error('Audio is blocked. Tap Replay or Retry Audio to continue.');
  return owned(owner, job);
}
function releaseJob(owner, job) {
  job.controller.abort(); job.cancel(false);
  if (job.source) { job.source.onended = null; try { job.source.stop(); } catch {} job.source = null; }
  if (job.media) { job.media.onended = null; job.media.onerror = null; job.media.onplaying = () => {}; job.media.pause(); job.media.src = ''; job.media = null; }
  for (const url of job.urls) { URL.revokeObjectURL(url); owner.urls.delete(url); } job.urls.clear();
  for (const done of [...owner.waits]) done(false);
}
function stopHint(owner) {
  owner.hintEpoch++; if (owner.hint) { try { owner.hint.stop(); } catch {} owner.hint = null; }
  if (owner.hintMedia) owner.hintMedia.pause();
}
function stopPlayback(owner) {
  owner.generation++; owner.intent++; owner.queue.cancelPending(); owner.paused = false; owner.setPaused(false);
  if (owner.job) releaseJob(owner, owner.job); owner.job = null; stopHint(owner); owner.publish('idle', '');
}
function primePlayback(owner) {
  if (!owner.job?.media && typeof Audio !== 'undefined') {
    for (const hint of [false, true]) {
      const media = mediaFor(owner, hint);
      const url = URL.createObjectURL(new Blob([confirmationWav()], { type: 'audio/wav' })); owner.urls.add(url);
      media.src = url; media.volume = 0;
      void Promise.resolve(media.play()).then(() => { if (!owner.alive) media.pause(); }).catch(() => {});
    }
  }
  const context = contextFor(owner);
  if (!context) return Promise.resolve(true);
  const unlocked = ensureAudioContextRunning(context, { onResumeSettled() { if (!owner.alive || owner.paused) void context.suspend?.(); } });
  if (context.createBuffer) { const source = context.createBufferSource(); source.buffer = context.createBuffer(1, 1, context.sampleRate || 24000); source.connect(context.destination); source.start(); source.onended = () => source.disconnect?.(); }
  return unlocked;
}
async function boundedAudio(response, signal) {
  if (response.headers.get('content-type')?.split(';')[0].toLowerCase() !== 'audio/wav') {
    let message='Private audio is unavailable. Retry or use captions.',result;
    try {const bytes=await readBoundedArrayBuffer(response,64*1024,'Audio error response is too large.',signal);result=JSON.parse(new TextDecoder().decode(bytes));if(typeof result.message==='string' && result.message.length<=500)message=result.message;}catch{}
    throw Object.assign(Error(message),{status:response.status,code:['RECORDED_AUDIO_MISSING','RECORDED_AUDIO_CONFLICT'].includes(result?.code)?result.code:undefined});
  }
  if (!response.ok) throw Error('Private audio is unavailable. Retry or use captions.');
  const maximum = typeof PREPARED_LINE_BYTES === 'number' ? PREPARED_LINE_BYTES : 32 * 1024 * 1024;
  const bytes = typeof readBoundedArrayBuffer === 'function' ? await readBoundedArrayBuffer(response, maximum, 'Private audio is too large.', signal) : await response.arrayBuffer();
  if (bytes.byteLength > maximum) throw Error('Private audio is too large.');
  inspectPcmWav(bytes); return bytes;
}
async function playChannel(owner, job, bytes, context, channel, first) {
  if (!await runningContext(owner, job)) return false;
  const audioContext = contextFor(owner), startsAt = first ? synchronizedStartTimestamp(context.startsAt, job.prompt.sequence?.startsAt) : undefined;
  const offset = context.serverClockOffsetMs ?? job.prompt.sequence?.serverClockOffsetMs;
  let channelReported = false;
  const started = () => { if (!owned(owner, job) || owner.paused || channelReported) return; channelReported = true; context.onChannelPlaybackStart?.({ channel: channel.voiceType }); };
  const start = () => { stage(owner, job, 'playing'); if (first) context.onPlaybackStart?.(); };
  if (audioContext && (channel.playbackSpeed??1)===1) {
    stage(owner, job, 'decoding');
    let buffer;
    try {buffer = await Promise.race([audioContext.decodeAudioData(bytes.slice(0)), job.cancelled]);}
    catch {if(!owned(owner,job))return false;}
    if(!owned(owner,job))return false;
    if(buffer){
    if (!await waitUntilResumed(owner, job)) return false;
    if (!await runningContext(owner, job)) return false;
    const source = audioContext.createBufferSource(), gain = audioContext.createGain(); job.source = source;
    source.buffer = buffer; gain.gain.value = owner.settings.volume ?? 1; source.connect(gain); gain.connect(audioContext.destination);
    const delay = synchronizedPlaybackDelayMs(startsAt, Date.now(), offset), when = synchronizedAudioContextStartTime(audioContext.currentTime, startsAt, Date.now(), offset);
    const finished = new Promise(resolve => { source.onended = () => { if (job.source === source) job.source = null; resolve(owned(owner, job)); }; });
    source.start(when);
    if (delay) { stage(owner, job, 'scheduled'); if (!await wait(owner, job, delay) || !await waitUntilResumed(owner, job)) return false; }
    start(); started(); return Promise.race([finished, job.cancelled]);
    }
  }
  const media = mediaFor(owner); job.media = media;
  const url = URL.createObjectURL(new Blob([bytes], { type: 'audio/wav' })); job.urls.add(url); owner.urls.add(url);
  media.src = url; media.volume = owner.settings.volume ?? 1; applyPitchPreservingSpeed(media, channel.playbackSpeed??1);
  const currentMedia = () => owned(owner, job) && job.media === media && media.src === url &&
    (typeof media.currentSrc !== 'string' || media.currentSrc === url);
  const finished = new Promise(resolve => {
    media.onended = () => { if (currentMedia() && media.ended !== false) { job.media = null; resolve(true); } };
    media.onerror = () => { if (currentMedia() && media.error !== null) resolve(false); };
    media.onplaying = () => { if (currentMedia() && media.paused !== true) started(); };
  });
  const delay = synchronizedPlaybackDelayMs(startsAt, Date.now(), offset);
  if (delay) { stage(owner, job, 'scheduled'); if (!await wait(owner, job, delay) || !await waitUntilResumed(owner, job)) return false; }
  if (!await waitUntilResumed(owner, job)) return false;
  start(); await media.play(); return Promise.race([finished, job.cancelled]);
}
async function performPrompt(owner, prompt, context, initial, generation) {
  if (!owner.alive || owner.generation !== generation) return false;
  let cancel; const cancelled = new Promise(resolve => { cancel = resolve; });
  const job = { prompt, generation, cancelled, cancel, controller: new AbortController(), urls: new Set(), stage: 'idle', media: null, source: null };
  owner.job = job; job.textFallback=false; stopHint(owner);
  try {
    if (!await runningContext(owner, job, initial)) return false;
    const channels = audioChannelsForPrompt(prompt);let hasPlayed=false;
    if (prompt.directionCaptionOnly) {
      if (context.allowTextFallback !== true) throw Error('This cue needs private captions for its unrecorded direction.');
      job.textFallback=true;context.onTextFallback?.({channel:'direction',captionOnly:true});
    }
    for (let i = 0; i < channels.length; i++) {
      if (hasPlayed) { stage(owner, job, 'between-channels'); if (!await wait(owner, job, DIRECTION_DIALOGUE_PAUSE_MS)) return false; }
      if (!await waitUntilResumed(owner, job)) return false;
      const channel = channels[i]; stage(owner, job, 'requesting');
      const route = context.practice ? '/api/rooms/' + encodeURIComponent(context.roomCode) + '/practice-audio' : '/api/voices/speech';
      const body = context.practice ? {text:channel.text,voiceId:channel.voiceType==='direction'?DIRECTION_VOICE_ID:context.voiceId,revision:context.revision,speed:owner.settings.voiceSpeed} :
        {roomCode:context.roomCode,token:context.token,promptId:prompt.id,channel:channel.voiceType,...(prompt.test?{purpose:channel.voiceType==='direction'?'test-direction':'test-dialogue'}:{})};
      const response = await Promise.race([fetch(sessionApiUrl(route), { method: 'POST', headers: { 'content-type': 'application/json',...(context.practice?{authorization:'Bearer '+context.token}:{}) },
        body: JSON.stringify(body),
        signal: AbortSignal.any([job.controller.signal, AbortSignal.timeout(AUDIO_REQUEST_TIMEOUT_MS)]), cache: 'no-store', redirect: 'error' }), cancelled]);
      if (!response || !owned(owner, job)) return false;
      const playbackSpeed=Number(response.headers.get('x-qline-playback-speed')||1);
      if(!isSupportedVoiceSpeed(playbackSpeed))throw Error('Recorded playback speed is unsupported.');
      let bytes;
      try {bytes=await boundedAudio(response,job.controller.signal);}
      catch(error){
        if(!owned(owner,job))return false;
        if(context.allowTextFallback!==true || prompt.test===true || error.status!==409 || error.code!=='RECORDED_AUDIO_MISSING')throw error;
        job.textFallback=true;context.onTextFallback?.({channel:channel.voiceType});continue;
      }
      const introPath=context.sceneIntroPath ?? prompt.sceneIntroAudio;
      if (i === 0 && typeof validSceneIntroPath === 'function' && validSceneIntroPath(introPath)) {
        const intro = await fetch(introPath, { signal: job.controller.signal, cache: 'no-store', redirect: 'error' });
        if(!intro.ok)throw Error('The scene introduction could not be loaded. Retry this opening.');
        bytes = prependSceneIntro(await boundedAudio(intro, job.controller.signal), bytes);
      }
      if (!owned(owner, job) || !await playChannel(owner, job, bytes, context, {...channel,playbackSpeed}, !hasPlayed)) return false;
      hasPlayed=true;
    }
    if(job.textFallback){const delay=synchronizedPlaybackDelayMs(synchronizedStartTimestamp(context.startsAt,prompt.sequence?.startsAt),Date.now(),context.serverClockOffsetMs??prompt.sequence?.serverClockOffsetMs);if(delay&&!await wait(owner,job,delay))return false;}
    if(job.textFallback && !await waitUntilResumed(owner,job))return false;
    stage(owner, job, job.textFallback?'finished-captions':'finished'); return owned(owner, job);
  } catch (error) { if (owned(owner, job)) owner.publish('failed', friendlyPlaybackError(error)); return false; }
  finally { if (owner.job === job) { owner.job = null; releaseJob(owner, job); } }
}
async function resumePlayback(owner) {
  const mine = ++owner.intent, job = owner.job, context = contextFor(owner);
  if (!job) { owner.paused = false; owner.setPaused(false); owner.publish('idle', ''); for (const done of [...owner.waits]) if (done.pauseWait) done(true); return true; }
  let result = true;
  try { if (context) result = await ensureAudioContextRunning(context, { onResumeSettled() { if (!owner.alive || owner.paused && owner.intent !== mine) void context.suspend?.(); } }); } catch { result = false; }
  if (!owner.alive || mine !== owner.intent) return false;
  if (!result) { if (job) releaseJob(owner, job); owner.job = null; owner.paused = false; owner.setPaused(false); owner.publish('failed', 'Audio is blocked. Tap Replay or Retry Audio to continue.'); return false; }
  owner.paused = false; owner.setPaused(false);
  if (job?.media && job.stage === 'playing') await job.media.play();
  owner.publish(job?.stage || 'idle', '');
  for (const done of [...owner.waits]) if (done.pauseWait) done(true);
  return true;
}
async function playHint(owner) {
  if (!owner.alive || owner.job || owner.paused || owner.settings.completionSound === false || owner.settings.volume === 0) return false;
  const mine = ++owner.hintEpoch, context = contextFor(owner), bytes = confirmationWav();
  if (context) {
    if (!await ensureAudioContextRunning(context)) return false;
    const buffer = await context.decodeAudioData(bytes);
    if (!owner.alive || mine !== owner.hintEpoch || owner.job || owner.paused) return false;
    const source = context.createBufferSource(), gain = context.createGain(); source.buffer = buffer; gain.gain.value = owner.settings.volume ?? 1;
    source.connect(gain); gain.connect(context.destination); owner.hint = source; source.onended = () => { if (owner.hint === source) owner.hint = null; }; source.start(); return true;
  }
  const media = mediaFor(owner, true), url = URL.createObjectURL(new Blob([bytes], { type: 'audio/wav' })); owner.urls.add(url);
  media.src = url; media.volume = owner.settings.volume ?? 1; await media.play(); return owner.alive && mine === owner.hintEpoch;
}
export function usePromptAudio() {
  const [settings, setSettings] = useState(() => ({ volume: 1, captions: true, voiceSpeed: 1, completionSound: true, ...savedAudioSettings() }));
  const [speaking, setSpeaking] = useState(false), [paused, setPaused] = useState(false), [lastError, setError] = useState(''), [playbackStage, setStage] = useState('idle');
  const [settingsNotice,setSettingsNotice]=useState('');
  const holder = useRef(null);
  if (!holder.current) holder.current = playbackOwner(settings, (stage, error) => { setStage(stage); setError(error); setSpeaking(['requesting', 'decoding', 'scheduled', 'playing', 'between-channels', 'paused'].includes(stage)); }, setPaused);
  const owner = holder.current;
  const speak = (prompt, context = {}) => {
    const generation = owner.generation, audioContext = contextFor(owner);
    const initial = !owner.paused && audioContext ? ensureAudioContextRunning(audioContext, { onResumeSettled() { if (!owner.alive || owner.paused) void audioContext.suspend?.(); } }).catch(() => false) : null;
    return owner.queue.enqueue(() => performPrompt(owner, prompt, context, initial, generation));
  };
  useEffect(() => {
    owner.alive = true;
    try {if(localStorage.getItem(SETTINGS_KEY)===null)localStorage.setItem(SETTINGS_KEY,JSON.stringify(owner.settings));setSettingsNotice('');}
    catch {setSettingsNotice('Audio settings were not saved. Your choices work on this page; allow storage and retry.');}
    const recover = () => { if (owner.alive && !owner.paused && owner.job && owner.context?.state === 'interrupted') void resumePlayback(owner); };
    document?.addEventListener?.('visibilitychange', recover); window.addEventListener?.('pageshow', recover); navigator.mediaDevices?.addEventListener?.('devicechange', recover);
    return () => { owner.alive = false; stopPlayback(owner); document?.removeEventListener?.('visibilitychange', recover); window.removeEventListener?.('pageshow', recover); navigator.mediaDevices?.removeEventListener?.('devicechange', recover); void owner.context?.close?.(); for (const url of owner.urls) URL.revokeObjectURL(url); owner.urls.clear(); };
  }, []);
  return { ...settings, speaking, paused, lastError, playbackStage, speak, settingsNotice,
    update(patch) { const next = { ...owner.settings, ...patch, voiceSpeed: normalizeVoiceSpeed(patch.voiceSpeed ?? owner.settings.voiceSpeed) }; owner.settings = next; setSettings(next); try { localStorage.setItem(SETTINGS_KEY, JSON.stringify(next)); setSettingsNotice(''); } catch {setSettingsNotice('Audio settings were not saved. Your choices work on this page; allow storage and retry.');} },
    unlock(prime = false) { return prime ? primePlayback(owner) : contextFor(owner) ? ensureAudioContextRunning(owner.context) : Promise.resolve(true); },
    playTest(context) { primePlayback(owner); return speak({ id: 'sound-test', test: true, directionText: 'Sound check.', dialogueText: 'Sound check.', type: 'dialogue' }, context); },
    stop() { stopPlayback(owner); }, pause() { owner.intent++; owner.paused = true; setPaused(true); owner.publish('paused', ''); void owner.context?.suspend?.(); owner.job?.media?.pause(); stopHint(owner); },
    resume() { return resumePlayback(owner); }, playCompletionHint() { return playHint(owner).catch(() => false); }, stopCompletionHint() { stopHint(owner); },
  };
}
